import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from '@/config/env';
import { useAuth } from '@/hooks/useAuth';
import { billingApi } from '../api/billingApi';
import type { BillingRedirectResponse } from '../types/billing';

export const billingKeys = {
  subscription: (organizationId?: string) =>
    ['billing-subscription', organizationId ?? 'none'] as const
};

const getOrganizationId = (user: ReturnType<typeof useAuth>['user']) => {
  const organization = user?.organization;

  if (organization && typeof organization === 'object') {
    return String(organization.id);
  }

  if (organization !== null && organization !== undefined) {
    return String(organization);
  }

  return undefined;
};

export const useBillingStatus = () => {
  const { user } = useAuth();
  const organizationId = getOrganizationId(user);

  return useQuery({
    queryKey: billingKeys.subscription(organizationId),
    queryFn: () => billingApi.getSubscription(),
    enabled: Boolean(user && env.billingEnabled),
    networkMode: 'online',
    staleTime: 60 * 1000
  });
};

export const useCreateCheckoutSession = () =>
  useMutation<BillingRedirectResponse, Error, string>({
    mutationFn: (planCode) =>
      billingApi.createCheckoutSession({
        plan_code: planCode || 'BASIC',
        success_url: `${window.location.origin}${window.location.pathname}?billing=success`,
        cancel_url: `${window.location.origin}${window.location.pathname}?billing=cancel`
      }),
    onSuccess: (response) => {
      window.location.assign(response.url);
    }
  });

export const useCreateCustomerPortalSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      billingApi.createCustomerPortalSession({
        return_url: window.location.href
      }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['billing-subscription'] });
      window.location.assign(response.url);
    }
  });
};

export const isBillingFrontendEnabled = () => env.billingEnabled;
